import { QueryState } from "@/components/query-state";
import { ImportButton } from "@/features/imports/import-dialog";
import { ObservabilityView } from "@/features/observability/website-metrics";
import { sourceLabel } from "@/lib/source-label";

/**
 * The routes, mirroring the existing application's page compositions.
 *
 * Each page assembles the same components in the same order; what was fetched
 * on the server there is fetched through the workspace client here, which is
 * the only difference an SPA forces.
 */

import Text from "@galaxy-io/dls/text/Text";
import { tableQuery } from "@gtm/contracts/core/saved-queries";
import { activitySortKeys, readSort } from "@gtm/contracts/core/sorting";
import { useQuery } from "@tanstack/react-query";
import { usePathname, useSearchParams } from "@/app/navigation";
import { PageHeader } from "@/components/workspace/page-header";
import { SignalsFilters } from "@/features/activity/activity-filters";
import { ActivityFeed } from "@/features/activity/people-feed";
import { SignalBoard } from "@/features/activity/signal-board";
import { SignalSwipe } from "@/features/activity/signal-swipe";
import { SignalsView } from "@/features/activity/signals-view";
import { CompanyDirectory } from "@/features/companies/company-directory";
import { ExclusionsView } from "@/features/exclusions/exclusions-view";
import { KnowledgeView } from "@/features/knowledge/knowledge-view";
import { QaView } from "@/features/knowledge/qa-view";
import { CreateEntityButton } from "@/features/people/entity-editor";
import { PeopleFilters } from "@/features/people/people-filters";
import { PeopleTable } from "@/features/people/people-table";
import { queryPresets } from "@/lib/query-presets";

function filtersFrom(params: URLSearchParams, keys: string[]) {
  return Object.fromEntries(keys.map((key) => [key, params.get(key) ?? undefined])) as Record<
    string,
    string | undefined
  >;
}

export function SignalsPage() {
  const params = useSearchParams();
  const preference = usePreferredSignalsView();

  const filters = filtersFrom(params, [
    "q",
    "assignee",
    "type",
    "repository",
    "days",
    "filter",
    "time",
    "topic",
    "review",
    "page",
    "sort",
    "direction",
  ]);

  const explicit = readSort(activitySortKeys, filters);
  const sorting = explicit.sort ? explicit : { sort: "when" as const, direction: "desc" as const };
  delete filters.sort;
  delete filters.direction;
  Object.assign(filters, sorting);

  const requested = params.get("view");
  const view =
    requested === "table" || requested === "board" || requested === "swipe"
      ? requested
      : preference;

  filters.view = view;
  // No status selection means all statuses, across every Signals view.
  filters.review = params.get("review") ?? "";

  return (
    <>
      <PageHeader title="Signals" actions={<ImportButton kind="signals" />} />
      <SignalsView view={view} />
      <SignalsFilters query={filters} />
      {view === "board" ? (
        <SignalBoard query={filters} />
      ) : view === "swipe" ? (
        <SignalSwipe key={JSON.stringify(filters)} query={filters} />
      ) : (
        <ActivityFeed key={JSON.stringify(filters)} query={filters} />
      )}
    </>
  );
}

/** The saved default from Preferences → General, until a URL says otherwise. */
function usePreferredSignalsView(): "board" | "table" | "swipe" {
  const { data } = useQuery({
    queryKey: ["preferences"],
    queryFn: async () => {
      const { unwrap, apiClient } = await import("@/lib/api-client");
      return unwrap(apiClient().GET("/profile/preferences", {}));
    },
    staleTime: 5 * 60_000,
  });
  const view = (data as { signalsView?: string } | undefined)?.signalsView;
  return view === "table" || view === "swipe" ? view : "board";
}

export function PeoplePage() {
  const params = useSearchParams();
  const filters = filtersFrom(params, [
    "q",
    "assignee",
    "filter",
    "state",
    "company",
    "tags",
    "page",
    "days",
    "repository",
    "sort",
    "direction",
  ]);
  return (
    <>
      <PageHeader
        title="People"
        actions={
          <>
            <ImportButton kind="people" />
            <CreateEntityButton kind="person" />
          </>
        }
      />
      <PeopleFilters query={filters} />
      <PeopleTable query={filters} />
    </>
  );
}

export function CompaniesPage() {
  return (
    <>
      <PageHeader
        title="Companies"
        actions={
          <>
            <ImportButton kind="companies" />
            <CreateEntityButton kind="company" />
          </>
        }
      />
      <CompanyDirectory />
    </>
  );
}

export function ExclusionsPage() {
  return <ExclusionsView />;
}

export function KnowledgePage() {
  return <KnowledgeView />;
}

export function QaPage() {
  return <QaView />;
}

// --- Workflows -------------------------------------------------------------

import { pageNumber, rangeDays } from "@gtm/contracts/core/people";
import { workflows as workflowCatalog, workflowName } from "@gtm/contracts/workflows/catalog";
import { useEffect } from "react";
import { useRouter } from "@/app/navigation";
import { CollectionRefresh } from "@/features/workflows/collection-refresh";
import { DeliveryHistory } from "@/features/workflows/delivery-history";
import { RunFilters } from "@/features/workflows/run-filters";
import { RunHistory } from "@/features/workflows/run-history";
import { WorkflowLauncher } from "@/features/workflows/workflow-launcher";
import { WorkflowSchedules } from "@/features/workflows/workflow-schedules";
import { WorkflowTabs } from "@/features/workflows/workflow-tabs";
import { apiClient, unwrap } from "@/lib/api-client";
import { date } from "@/lib/utils";

const RUN_FILTER_KEYS = ["q", "workflow", "status", "trigger", "repository", "days", "page"];

export function WorkflowsPage() {
  const params = useSearchParams();
  const router = useRouter();

  const view = params.get("view");
  const kind = params.get("kind");
  const runId = params.get("runId");
  const deliveryId = params.get("deliveryId");

  /**
   * A drawer link can arrive without the view that owns it. Rather than render
   * an empty tab, the URL is corrected to the one that can show the record.
   */
  useEffect(() => {
    const misplacedDelivery = deliveryId && (kind !== "delivery" || view !== "history");
    const misplacedRun = runId && !deliveryId && view !== "history";
    if (!misplacedDelivery && !misplacedRun) return;
    const next = new URLSearchParams(window.location.search);
    next.set("view", "history");
    next.set("kind", deliveryId ? "delivery" : "collection");
    router.replace(`/workflows?${next}`);
  }, [deliveryId, runId, kind, view, router]);

  const sources = useQuery({
    queryKey: ["collection-sources"],
    queryFn: () => unwrap(apiClient().GET("/workflows/sources", {})),
  });
  const latest = useQuery({
    queryKey: ["latest-runs"],
    queryFn: () => unwrap(apiClient().GET("/workflows/runs/latest", {})),
    refetchInterval: 10_000,
  });
  const active = latest.data?.active ?? false;

  if (view === "schedules") {
    return (
      <>
        <PageHeader title="Workflows" />
        <WorkflowTabs view="schedules" />
        {sources.isPending || sources.error ? (
          <QueryState
            error={sources.error}
            loadingMessage="Loading sources…"
            onRetry={() => void sources.refetch()}
          />
        ) : (
          <WorkflowSchedules repositories={sources.data?.sources ?? []} />
        )}
      </>
    );
  }

  const history = view === "history" || !!runId || !!deliveryId;

  if (kind === "delivery" || deliveryId) {
    return (
      <>
        <PageHeader title="Workflows" />
        <WorkflowTabs view="deliveries" />
        <DeliveryHistory />
      </>
    );
  }

  if (history) {
    const page = pageNumber(params.get("page") ?? undefined);
    const filters = Object.fromEntries(
      RUN_FILTER_KEYS.map((key) => [key, params.get(key) || undefined]),
    );
    return (
      <>
        <PageHeader title="Workflows" />
        <WorkflowTabs view="runs" />
        <CollectionRefresh showToolbar active={active}>
          <RunFilters query={filters} repositories={sources.data?.sources ?? []} />
        </CollectionRefresh>
        <RunList page={page} filters={filters} days={rangeDays(filters.days)} />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Workflows" />
      <WorkflowTabs view="library" />
      <CollectionRefresh active={active} />
      {sources.isPending || sources.error ? (
        <QueryState
          error={sources.error}
          loadingMessage="Loading workflows…"
          onRetry={() => void sources.refetch()}
        />
      ) : (
        <WorkflowLauncher
          repositories={sources.data?.sources ?? []}
          lastRuns={latest.data?.runs ?? []}
          initialCatalog={[...workflowCatalog]}
        />
      )}
    </>
  );
}

/** Run rows come from Temporal, so they are fetched rather than server-rendered. */
function RunList({
  page,
  filters,
  days,
}: {
  page: number;
  filters: Record<string, string | undefined>;
  days: number;
}) {
  const runs = useQuery({
    queryKey: ["runs", filters, page, days],
    queryFn: () =>
      unwrap(
        apiClient().GET("/workflows/runs", {
          params: { query: { ...filters, page, days } },
        }),
      ),
    refetchInterval: 5000,
  });

  const rows = (runs.data?.rows ?? []).map((run) => ({
    id: run.workflowId,
    workflow: workflowName(run.workflowKey),
    workflowKey: run.workflowKey,
    repository: sourceLabel(run.workflowKey.split(".")[0], run.sourceName),
    status: run.status,
    requested: date(run.startedAt, true),
  }));

  return (
    <RunHistory
      loading={runs.isPending}
      error={runs.error ?? undefined}
      onRetry={() => void runs.refetch()}
      page={page}
      hasNext={runs.data?.hasNext ?? false}
      total={runs.data?.total ?? 0}
      query={{ ...filters, view: "history", kind: "collection" }}
      rows={rows}
    />
  );
}

// --- Integrations, data and settings ---------------------------------------

import { period } from "@gtm/contracts/core/people";
import { ActivityFilters } from "@/features/activity/activity-filters";
import { MembersView } from "@/features/authentication/members-view";
import { PreferencesView } from "@/features/authentication/preferences-view";
import { SettingsColumn, SettingsTabs } from "@/features/authentication/settings-header";
import { QueryEditor } from "@/features/console/query-editor";
import { IntegrationSettings } from "@/features/integrations/integration-settings";
import { ObservabilityMetrics } from "@/features/observability/observability-metrics";

export function IntegrationsPage() {
  const connections = useQuery({
    queryKey: ["integrations"],
    queryFn: () => unwrap(apiClient().GET("/integrations", {})),
  });
  const repositories = useQuery({
    queryKey: ["github-repositories"],
    queryFn: () => unwrap(apiClient().GET("/integrations/github/sources", {})),
  });

  const error = connections.error ?? repositories.error;
  if (connections.isPending || repositories.isPending || error) {
    return (
      <>
        <PageHeader title="Integrations" />
        <QueryState
          error={error}
          loadingMessage="Loading integrations…"
          onRetry={() => {
            void connections.refetch();
            void repositories.refetch();
          }}
        />
      </>
    );
  }

  return (
    <IntegrationSettings
      connections={connections.data ?? []}
      repositories={repositories.data ?? []}
    />
  );
}

export function ObservabilityPage() {
  return (
    <ObservabilityView>
      <AudienceObservability />
    </ObservabilityView>
  );
}

function AudienceObservability() {
  const params = useSearchParams();
  const filters = {
    days: params.get("days") ?? undefined,
    repository: params.get("repository") ?? undefined,
  };

  const overview = useQuery({
    queryKey: ["audience-overview", filters],
    queryFn: () =>
      unwrap(
        apiClient().GET("/reporting/audience", {
          params: { query: { days: period(filters.days), repository: filters.repository } },
        }),
      ),
  });
  const options = useQuery({
    queryKey: ["activity-filters"],
    queryFn: () => unwrap(apiClient().GET("/activity/filters", {})),
  });

  if (!overview.data)
    return (
      <QueryState
        error={overview.error}
        loadingMessage="Loading metrics…"
        onRetry={() => void overview.refetch()}
      />
    );

  return (
    <>
      <ActivityFilters
        overview
        query={filters}
        repositories={options.data?.resources ?? []}
        types={options.data?.types ?? []}
      />
      <ObservabilityMetrics overview={overview.data} />
    </>
  );
}

export function ConsolePage() {
  const params = useSearchParams();
  const id = params.get("id");
  const dataset = params.get("dataset");
  const preset = params.get("preset");
  const schema = useQuery({
    queryKey: ["console-schema"],
    queryFn: () => unwrap(apiClient().GET("/console/schema", {})),
  });
  const saved = useQuery({
    queryKey: ["saved-queries"],
    queryFn: () => unwrap(apiClient().GET("/console/queries", {})),
    enabled: !!id,
  });
  if ((id && (saved.isPending || saved.error)) || schema.isPending || schema.error)
    return (
      <>
        <PageHeader title="Console" />
        <QueryState
          error={saved.error ?? schema.error}
          loadingMessage="Loading console…"
          onRetry={() => {
            void saved.refetch();
            void schema.refetch();
          }}
        />
      </>
    );
  const initialQuery =
    params.get("sql") ??
    (id ? saved.data?.find((query) => query.id === id)?.query : undefined) ??
    (dataset && schema.data?.[dataset] ? tableQuery(dataset, schema.data[dataset]) : undefined) ??
    (preset && preset in queryPresets ? queryPresets[preset as keyof typeof queryPresets].sql : "");
  return (
    <QueryEditor
      key={`${id ?? ""}:${dataset ?? ""}:${preset ?? ""}:${params.get("sql") ?? ""}:${params.get("new") ?? ""}`}
      initialQuery={initialQuery}
      schema={schema.data ?? {}}
    />
  );
}

// Settings tabs retain their existing workspace-aware routes.
export function MembersPage() {
  return <PreferencesPage />;
}

export function PreferencesPage() {
  const workspace = usePathname() === "/settings/members";
  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => unwrap(apiClient().GET("/profile", {})),
  });
  const preferences = useQuery({
    queryKey: ["preferences"],
    queryFn: () => unwrap(apiClient().GET("/profile/preferences", {})),
  });

  return (
    <>
      <PageHeader title="Settings" />
      <SettingsTabs workspace={workspace} isAdmin={profile.data?.role === "admin"} />
      <SettingsColumn>
        {profile.isPending || preferences.isPending || profile.error || preferences.error ? (
          <QueryState
            error={profile.error ?? preferences.error}
            loadingMessage="Loading settings…"
            onRetry={() => {
              void profile.refetch();
              void preferences.refetch();
            }}
          />
        ) : profile.data && preferences.data ? (
          workspace ? (
            profile.data.role === "admin" ? (
              <MembersView
                currentUserId={profile.data.userId}
                workspaceName={profile.data.workspaceName}
              />
            ) : (
              <Text>Workspace settings are available to admins.</Text>
            )
          ) : (
            <PreferencesView profile={profile.data} preferences={preferences.data} />
          )
        ) : null}
      </SettingsColumn>
    </>
  );
}
